import React, { useEffect, useState, useRef } from "react";
import { FaCode, FaRocket, FaHandshake, FaUsers, FaAward, FaChartLine } from "react-icons/fa";
import { motion } from "framer-motion";

const CountUp = ({ end, duration = 3000, suffix = "", label }) => {
  const [count, setCount] = useState(0);
  const countRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          let start = 0;
          const increment = end / (duration / 20);
          
          const timer = setInterval(() => {
            start += increment;
            if (start >= end) {
              setCount(end);
              clearInterval(timer);
            } else {
              setCount(Math.floor(start));
            }
          }, 20);

          return () => clearInterval(timer);
        }
      },
      { threshold: 0.5 }
    );

    if (countRef.current) observer.observe(countRef.current);
    return () => observer.disconnect();
  }, [end, duration]);

  return (
    <div className="stat-counter" ref={countRef}>
      <span className="counter-number">
        {count}{suffix}
      </span>
      <span className="counter-label">{label}</span>
    </div>
  );
};

const About = () => {
  const sectionRef = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setInView(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="about" className="about" ref={sectionRef}>
      {/* Background Elements */}
      <div className="background-elements">
        <div className="floating-circle circle-1"></div>
        <div className="floating-circle circle-2"></div>
        <div className="floating-circle circle-3"></div>
      </div>

      <div className="container">
        {/* Section Header */}
        <div className="section-header">
          <div className="section-badge">
            <span className="badge-dot"></span>
            <span className="badge-text">About Us</span>
          </div>
          
          <h1 className="section-title">
            <span className="title-line title-line-1">Driving Digital</span>
            <span className="title-line title-line-2">
              <span className="gradient-text">Transformation</span> Through
            </span>
            <span className="title-line title-line-3">Innovative Technology</span>
          </h1>
        </div>

        {/* About Content */}
        <div className="about-content-grid">
          <div className="about-text">
            <p className="about-intro">
              Codriva Tech is a premier IT development company headquartered in Gwalior, 
              dedicated to delivering cutting-edge digital solutions that empower businesses 
              to thrive in today's fast-paced technology landscape.
            </p>
            
            <div className="highlight-section">
              <div className="highlight-item">
                <div className="highlight-icon">
                  <FaCode />
                </div>
                <div className="highlight-content">
                  <h4>Technical Excellence</h4>
                  <p>Mastery of the latest technologies and frameworks ensuring high-quality solutions.</p>
                </div>
              </div>
              
              <div className="highlight-item">
                <div className="highlight-icon">
                  <FaRocket />
                </div>
                <div className="highlight-content">
                  <h4>Agile Delivery</h4>
                  <p>Fast, reliable project execution with a focus on quality and deadlines.</p>
                </div>
              </div>
              
              <div className="highlight-item">
                <div className="highlight-icon">
                  <FaHandshake />
                </div>
                <div className="highlight-content">
                  <h4>Client Partnership</h4>
                  <p>Collaborative approach tailored to your unique business challenges.</p>
                </div>
              </div>
            </div>
          </div>

          <div className="about-visual">
            <div className="visual-card">
              <div className="card-content">
                <h3>Our Mission</h3>
                <p>
                  Transform ideas into powerful digital solutions that drive business growth, 
                  enhance efficiency, and create lasting impact through innovative technology.
                </p>
              </div>
              <div className="card-glow"></div>
            </div>
          </div>
        </div>

        {/* Statistics Section */}
        <div className="stats-section">
          <div className="stats-grid">
            <div className="stat-item">
              <div className="stat-icon">
                <FaChartLine />
              </div>
              <CountUp end={100} suffix="+" label="Projects Delivered" />
            </div>
            
            <div className="stat-item">
              <div className="stat-icon">
                <FaAward />
              </div>
              <CountUp end={98} suffix="%" label="Client Satisfaction" />
            </div>
            
            <div className="stat-item">
              <div className="stat-icon">
                <FaUsers />
              </div>
              <CountUp end={3} suffix="+" label="Years Experience" />
            </div>
            
            <div className="stat-item">
              <div className="stat-icon">
                <FaRocket />
              </div>
              <div className="stat-counter">
                <span className="counter-number">24/7</span>
                <span className="counter-label">Dedicated Support</span>
              </div>
            </div>
          </div>
        </div>

        {/* Value Proposition */}
        <div className="value-proposition">
          <h3 className="value-title">Why Choose Codriva Tech?</h3>
          <div className="value-grid">
            <div className="value-card">
              <div className="value-icon-wrapper">
                <FaCode className="value-icon" />
              </div>
              <h4>Cutting-Edge Technology</h4>
              <p>We leverage the latest tech stack to build scalable, future-proof solutions.</p>
            </div>
            
            <div className="value-card">
              <div className="value-icon-wrapper">
                <FaUsers className="value-icon" />
              </div>
              <h4>Expert Team</h4>
              <p>Our skilled professionals bring years of experience across diverse domains.</p>
            </div>
            
            <div className="value-card">
              <div className="value-icon-wrapper">
                <FaHandshake className="value-icon" />
              </div>
              <h4>Transparent Process</h4>
              <p>Clear communication and regular updates throughout the development cycle.</p>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .about {
          padding: 120px 0;
          background: linear-gradient(135deg, 
            #020617 0%,
            #0f172a 30%,
            #1e293b 70%,
            #0f172a 100%);
          color: #f8fafc;
          position: relative;
          overflow: hidden;
        }

        /* Background Elements */
        .background-elements {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          pointer-events: none;
        }

        .floating-circle {
          position: absolute;
          border-radius: 50%;
          border: 1px solid rgba(34, 211, 238, 0.1);
          animation: float 8s ease-in-out infinite;
        }

        .circle-1 {
          width: 300px;
          height: 300px;
          top: 10%;
          left: 5%;
          animation-delay: 0s;
        }

        .circle-2 {
          width: 200px;
          height: 200px;
          bottom: 20%;
          right: 10%;
          animation-delay: -2s;
          animation-duration: 10s;
        }

        .circle-3 {
          width: 150px;
          height: 150px;
          top: 50%;
          left: 80%;
          animation-delay: -4s;
          animation-duration: 12s;
        }

        .container {
          max-width: 1800px;
          margin: 0 auto;
          padding: 0 32px;
          position: relative;
          z-index: 2;
        }

        /* Section Header */
        .section-header {
          text-align: center;
          margin-bottom: 80px;
        }

        .section-badge {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          background: rgba(34, 211, 238, 0.1);
          padding: 10px 24px;
          border-radius: 50px;
          margin-bottom: 32px;
          opacity: 0;
          animation: fadeIn 1s ease 0.2s forwards;
        }

        .badge-dot {
          width: 8px;
          height: 8px;
          background: #22d3ee;
          border-radius: 50%;
          animation: pulse 2s ease-in-out infinite;
        }

        .badge-text {
          font-size: 0.9rem;
          font-weight: 600;
          color: #94a3b8;
          letter-spacing: 1px;
        }

        .section-title {
          font-size: 4rem;
          font-weight: 800;
          line-height: 1.1;
          margin-bottom: 24px;
        }

        .title-line {
          display: block;
          opacity: 0;
          transform: translateY(30px);
          animation: slideUp 1s ease forwards;
        }

        .title-line-1 {
          animation-delay: 0.4s;
        }

        .title-line-2 {
          animation-delay: 0.6s;
        }

        .title-line-3 {
          animation-delay: 0.8s;
        }

        .gradient-text {
          background: linear-gradient(135deg, #22d3ee 0%, #3b82f6 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        /* About Content Grid */
        .about-content-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 60px;
          margin-bottom: 100px;
          align-items: center;
        }

        .about-text {
          opacity: 0;
          transform: translateX(-50px);
          animation: slideInLeft 1.2s ease 1s forwards;
        }

        .about-intro {
          font-size: 1.25rem;
          line-height: 1.8;
          color: #cbd5e1;
          margin-bottom: 40px;
        }

        .highlight-section {
          display: flex;
          flex-direction: column;
          gap: 30px;
        }

        .highlight-item {
          display: flex;
          gap: 20px;
          align-items: flex-start;
          opacity: 0;
          transform: translateY(20px);
          animation: fadeInUp 0.8s ease forwards;
        }

        .highlight-item:nth-child(1) {
          animation-delay: 1.2s;
        }

        .highlight-item:nth-child(2) {
          animation-delay: 1.4s;
        }

        .highlight-item:nth-child(3) {
          animation-delay: 1.6s;
        }

        .highlight-icon {
          width: 60px;
          height: 60px;
          background: rgba(34, 211, 238, 0.1);
          border-radius: 16px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #22d3ee;
          font-size: 1.5rem;
          flex-shrink: 0;
        }

        .highlight-content h4 {
          font-size: 1.2rem;
          font-weight: 600;
          margin-bottom: 8px;
          color: #f8fafc;
        }

        .highlight-content p {
          font-size: 0.95rem;
          line-height: 1.6;
          color: #94a3b8;
        }

        .about-visual {
          opacity: 0;
          transform: translateX(50px);
          animation: slideInRight 1.2s ease 1s forwards;
        }

        .visual-card {
          position: relative;
          padding: 40px;
          background: rgba(255, 255, 255, 0.02);
          border: 1px solid rgba(255, 255, 255, 0.05);
          border-radius: 24px;
          backdrop-filter: blur(10px);
          overflow: hidden;
        }

        .card-glow {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: radial-gradient(circle at center, 
            rgba(34, 211, 238, 0.05) 0%,
            transparent 70%);
          z-index: -1;
        }

        .visual-card h3 {
          font-size: 1.8rem;
          font-weight: 700;
          margin-bottom: 20px;
          color: #22d3ee;
        }

        .visual-card p {
          font-size: 1.1rem;
          line-height: 1.7;
          color: #cbd5e1;
        }

        /* Statistics Section */
        .stats-section {
          margin-bottom: 100px;
          opacity: 0;
          animation: fadeIn 1s ease 1.8s forwards;
        }

        .stats-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 30px;
        }

        .stat-item {
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: 40px 20px;
          background: rgba(255, 255, 255, 0.02);
          border: 1px solid rgba(255, 255, 255, 0.05);
          border-radius: 20px;
          backdrop-filter: blur(10px);
          transition: transform 0.4s ease, border-color 0.4s ease;
        }

        .stat-item:hover {
          transform: translateY(-10px);
          border-color: rgba(34, 211, 238, 0.2);
        }

        .stat-icon {
          width: 60px;
          height: 60px;
          background: rgba(34, 211, 238, 0.1);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #22d3ee;
          font-size: 1.5rem;
          margin-bottom: 20px;
        }

        .stat-counter {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 10px;
        }

        .counter-number {
          font-size: 3.5rem;
          font-weight: 800;
          background: linear-gradient(135deg, #22d3ee, #3b82f6);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          line-height: 1;
        }

        .counter-label {
          font-size: 1rem;
          color: #94a3b8;
          font-weight: 500;
          text-align: center;
        }

        /* Value Proposition */
        .value-proposition {
          opacity: 0;
          animation: fadeIn 1s ease 2s forwards;
        }

        .value-title {
          font-size: 2.5rem;
          font-weight: 700;
          text-align: center;
          margin-bottom: 60px;
          color: #f8fafc;
        }

        .value-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 30px;
        }

        .value-card {
          padding: 40px 30px;
          background: rgba(255, 255, 255, 0.02);
          border: 1px solid rgba(255, 255, 255, 0.05);
          border-radius: 20px;
          text-align: center;
          backdrop-filter: blur(10px);
          transition: all 0.4s ease;
        }

        .value-card:hover {
          transform: translateY(-10px);
          border-color: rgba(34, 211, 238, 0.2);
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
        }

        .value-icon-wrapper {
          width: 80px;
          height: 80px;
          background: rgba(34, 211, 238, 0.1);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 30px;
          transition: transform 0.4s ease;
        }

        .value-card:hover .value-icon-wrapper {
          transform: scale(1.1) rotate(5deg);
        }

        .value-icon {
          color: #22d3ee;
          font-size: 2rem;
        }

        .value-card h4 {
          font-size: 1.3rem;
          font-weight: 600;
          margin-bottom: 15px;
          color: #f8fafc;
        }

        .value-card p {
          font-size: 0.95rem;
          line-height: 1.6;
          color: #94a3b8;
        }

        /* Animations */
        @keyframes fadeIn {
          to {
            opacity: 1;
          }
        }

        @keyframes slideUp {
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes slideInLeft {
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes slideInRight {
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes fadeInUp {
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes float {
          0%, 100% {
            transform: translateY(0) rotate(0deg);
          }
          50% {
            transform: translateY(-20px) rotate(180deg);
          }
        }

        @keyframes pulse {
          0%, 100% {
            opacity: 1;
            transform: scale(1);
          }
          50% {
            opacity: 0.6;
            transform: scale(1.2);
          }
        }

        /* Responsive Design */
        @media (max-width: 1024px) {
          .about-content-grid {
            grid-template-columns: 1fr;
            gap: 40px;
          }

          .stats-grid {
            grid-template-columns: repeat(2, 1fr);
          }

          .value-grid {
            grid-template-columns: repeat(2, 1fr);
          }

          .section-title {
            font-size: 3rem;
          }
        }

        @media (max-width: 768px) {
          .about {
            padding: 100px 0;
          }

          .section-title {
            font-size: 2.5rem;
          }

          .stats-grid {
            grid-template-columns: 1fr;
          }

          .value-grid {
            grid-template-columns: 1fr;
          }

          .stat-item {
            padding: 30px 20px;
          }

          .counter-number {
            font-size: 2.8rem;
          }

          .visual-card {
            padding: 30px;
          }
        }

        @media (max-width: 480px) {
          .container {
            padding: 0 20px;
          }

          .section-title {
            font-size: 2rem;
          }

          .highlight-item {
            flex-direction: column;
            text-align: center;
          }

          .highlight-icon {
            margin: 0 auto;
          }

          .value-card {
            padding: 30px 20px;
          }
        }
      `}</style>
    </section>
  );
};

export default About;